// ###### IMPORTS
import React, { useState, useEffect } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-hot-toast';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import { useFinanceProfile } from '../../moduleServices/finance_profilecontext';
import {
  fetchAvailableCurrencies,
  createUserCurrency,
  deleteUserCurrency,
} from '../../moduleServices/flow_requests';

// ###### COMPONENT
const CardUserCurrencies = () => {
  const { currencies, reloadCurrencies, loading: loadingProfile } = useFinanceProfile();
  const [available, setAvailable] = useState([]);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [addSelectValue, setAddSelectValue] = useState('');

  const userCodes = (currencies || []).filter(c => c.is_active !== false).map(c => c.currency_code);

  useEffect(() => {
    const load = async () => {
      setLoadingAvailable(true);
      try {
        const list = await fetchAvailableCurrencies();
        setAvailable(Array.isArray(list) ? list : []);
      } catch {
        setAvailable([]);
      } finally {
        setLoadingAvailable(false);
      }
    };
    load();
  }, []);

  const optionsToAdd = available.filter(
    item => item.code && !userCodes.includes(item.code)
  );

  const handleAdd = async () => {
    const item = available.find(i => i.code === addSelectValue);
    if (!item) {
      toast.error('Select a currency to add.');
      return;
    }
    setAdding(true);
    try {
      await createUserCurrency({
        currency_code: item.code,
        currency_symbol: item.symbol || item.code,
        currency_name: item.name || item.code,
      });
      setAddSelectValue('');
      await reloadCurrencies();
      toast.success(`${item.code} added.`);
    } catch (err) {
      const msg = err.response?.data?.currency_code?.[0] || err.message || 'Failed to add currency';
      toast.error(msg);
    } finally {
      setAdding(false);
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Remove ${code} from your currencies?`)) return;
    setDeletingId(id);
    try {
      await deleteUserCurrency(id);
      await reloadCurrencies();
      toast.success(`${code} removed.`);
    } catch (err) {
      toast.error(err.message || 'Failed to remove currency');
    } finally {
      setDeletingId(null);
    }
  };

  const list = (currencies || []).filter(c => c.is_active !== false);

  return (
    <div className="bg-white p-3 rounded-2xl shadow flex flex-col mb-2">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">Your currencies</h2>
      </div>
      {loadingProfile ? (
        <div className="flex justify-center py-4"><SpinnerLoad /></div>
      ) : (
        <>
          <ul className="space-y-2 mb-3">
            {list.map((c) => (
              <li
                key={c.id}
                className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 border border-gray-100"
              >
                <span className="font-medium text-gray-800">{c.currency_code}</span>
                <span className="text-gray-600 mx-2">{c.currency_symbol}</span>
                <span className="text-gray-500 text-sm flex-1 truncate mr-2">{c.currency_name || '—'}</span>
                <button
                  type="button"
                  onClick={() => handleDelete(c.id, c.currency_code)}
                  disabled={deletingId === c.id || list.length <= 1}
                  className="p-1.5 rounded text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  title="Remove currency"
                >
                  {deletingId === c.id ? (
                    <SpinnerLoad className="w-4 h-4" />
                  ) : (
                    <TrashIcon className="w-4 h-4" />
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-gray-100">
            <label className="text-xs text-gray-600 font-medium">Add currency (ISO 4217)</label>
            <select
              value={addSelectValue}
              onChange={(e) => setAddSelectValue(e.target.value)}
              disabled={loadingAvailable || adding}
              className="border rounded-md px-2 py-1.5 text-sm min-w-[120px]"
            >
              <option value="">— Select —</option>
              {optionsToAdd.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.code} {item.symbol ? `(${item.symbol})` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!addSelectValue || adding || loadingAvailable}
              className="inline-flex items-center gap-1 bg-gray-700 hover:bg-gray-800 text-white px-3 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              {adding ? <SpinnerLoad className="w-4 h-4" /> : <PlusIcon className="w-4 h-4" />}
              Add
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default CardUserCurrencies;
